import React, { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AREAS_OF_INTEREST } from "@/data/siteContent";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(6, "Enter a valid phone number"),
  child_grade: z.string().min(1, "Please share your child's age or grade"),
  area_of_interest: z.string().min(1, "Please choose an area"),
  message: z.string().max(2000).optional().or(z.literal("")),
});

export default function ContactModal({ open, onOpenChange }) {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      child_grade: "",
      area_of_interest: "",
      message: "",
    },
  });

  const areaValue = watch("area_of_interest");

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      toast.success("Thank you! Your request has been received.", {
        description: "An advisor will reach out within one business day.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : "") ||
        "Something went wrong. Please try again.";
      toast.error("Submission failed", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-modal"
        className="max-w-2xl bg-white border-navy/10 p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-navy text-white px-6 md:px-10 py-8 relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
          <DialogHeader className="space-y-2 text-left">
            <p className="text-gold text-[10px] md:text-[11px] tracking-[0.3em] uppercase">
              — Free Consultation
            </p>
            <DialogTitle className="font-serif text-white text-3xl md:text-4xl leading-tight">
              Let's talk about your child's journey.
            </DialogTitle>
            <DialogDescription className="text-white/75 text-sm md:text-base">
              Share a few details and an advisor will reach out within one business day.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="px-6 md:px-10 py-8 space-y-5 bg-white"
          data-testid="contact-form"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Full Name" error={errors.full_name?.message}>
              <Input
                data-testid="contact-field-full-name"
                placeholder="Jane Doe"
                {...register("full_name")}
                className="border-navy/20 focus-visible:ring-gold focus-visible:border-gold"
              />
            </Field>
            <Field label="Email" error={errors.email?.message}>
              <Input
                data-testid="contact-field-email"
                type="email"
                placeholder="jane@example.com"
                {...register("email")}
                className="border-navy/20 focus-visible:ring-gold focus-visible:border-gold"
              />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Phone Number" error={errors.phone?.message}>
              <Input
                data-testid="contact-field-phone"
                placeholder="+91 98xxxxxxxx"
                {...register("phone")}
                className="border-navy/20 focus-visible:ring-gold focus-visible:border-gold"
              />
            </Field>
            <Field label="Client's Age / Grade" error={errors.child_grade?.message}>
              <Input
                data-testid="contact-field-child-grade"
                placeholder="e.g. 15 / Grade 10"
                {...register("child_grade")}
                className="border-navy/20 focus-visible:ring-gold focus-visible:border-gold"
              />
            </Field>
          </div>

          <Field label="Area of Interest" error={errors.area_of_interest?.message}>
            <Select
              value={areaValue || ""}
              onValueChange={(val) =>
                setValue("area_of_interest", val, { shouldValidate: true })
              }
            >
              <SelectTrigger
                data-testid="contact-field-area"
                className="border-navy/20 focus:ring-gold focus:border-gold"
              >
                <SelectValue placeholder="Choose an area" />
              </SelectTrigger>
              <SelectContent className="z-[60]">
                {AREAS_OF_INTEREST.map((a) => (
                  <SelectItem key={a} value={a} data-testid={`area-option-${a.replace(/\s/g, "-").toLowerCase()}`}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Message (optional)" error={errors.message?.message}>
            <Textarea
              data-testid="contact-field-message"
              rows={4}
              placeholder="Tell us a bit about your child's goals..."
              {...register("message")}
              className="border-navy/20 focus-visible:ring-gold focus-visible:border-gold resize-none"
            />
          </Field>

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy font-semibold py-4 px-6 tracking-wide uppercase text-sm rounded-sm hover:bg-gold-hover transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_18px_40px_-18px_rgba(245,197,24,0.6)]"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Sending..." : "Send Request"}
            </button>
            <p className="mt-3 text-center text-xs text-navy-deeper/50">
              No commitment. No spam. Just clarity.
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-navy/70 font-medium">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
